<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminAuthStore, type AdminRole } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import AdminBreadcrumb from './AdminBreadcrumb.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import { Menu, Bell, ChevronDown, LogOut } from 'lucide-vue-next'

// ---------------------------------------------------------------------------
// Stores & router
// ---------------------------------------------------------------------------

const authStore = useAdminAuthStore()
const uiStore = useUiStore()
const router = useRouter()

const isCollapsed = computed(() => uiStore.sidebarCollapsed)

function toggleSidebar() {
  uiStore.toggleSidebar()
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const dropdownOpen = ref(false)
const dropdownRef = ref<HTMLDivElement | null>(null)

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const adminName = computed(() => authStore.adminUser?.display_name ?? 'Admin')
const adminEmail = computed(() => authStore.adminUser?.email ?? '')
const adminRole = computed<AdminRole | null>(() => authStore.adminUser?.admin_role ?? null)

const initials = computed(() => {
  const name = adminName.value
  if (!name) return 'A'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

const roleLabels: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  support: 'Support',
  billing: 'Billing',
}

const roleBadgeClasses: Record<AdminRole, string> = {
  super_admin: 'text-danger',
  support: 'text-primary',
  billing: 'text-success',
}

// ---------------------------------------------------------------------------
// Dropdown
// ---------------------------------------------------------------------------

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function closeDropdown() {
  dropdownOpen.value = false
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
})

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

async function handleLogout() {
  closeDropdown()
  await authStore.logout()
  router.push('/admin/login')
}
</script>

<template>
  <header
    class="h-16 bg-bg-primary border-b border-border flex items-center justify-between px-6 flex-shrink-0 z-sticky"
    data-testid="admin-header"
  >
    <!-- Left: Logo area (matches sidebar width) + Breadcrumb -->
    <div class="flex items-center min-w-0 flex-1">
      <!-- Logo container — same width as sidebar -->
      <div
        :class="[
          'flex items-center h-16 flex-shrink-0 border-r border-border -ml-6 px-4 transition-all duration-base',
          isCollapsed ? 'w-[70px] justify-center' : 'w-[240px]',
        ]"
      >
        <div class="flex items-center gap-2.5 min-w-0">
          <div
            class="w-8 h-8 bg-primary rounded-md flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <span class="text-white text-sm font-bold">P</span>
          </div>
          <span
            v-if="!isCollapsed"
            class="text-md font-semibold text-text-primary whitespace-nowrap"
          >
            Postlytics
          </span>
        </div>
      </div>

      <!-- Collapse toggle + Breadcrumb -->
      <div class="flex items-center gap-3 pl-3 min-w-0">
        <button
          class="flex items-center justify-center w-7 h-7 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-secondary transition-colors duration-fast flex-shrink-0"
          :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          data-testid="header-sidebar-toggle"
          @click="toggleSidebar"
        >
          <Menu :size="16" aria-hidden="true" />
        </button>

        <AdminBreadcrumb />
      </div>
    </div>

    <!-- Right: Actions -->
    <div class="flex items-center gap-4">
      <!-- Notification bell placeholder -->
      <BaseButton
        variant="icon"
        aria-label="Notifications"
        :disabled="true"
        data-testid="header-notification-bell"
      >
        <Bell :size="18" class="text-text-secondary" aria-hidden="true" />
      </BaseButton>

      <!-- Admin user dropdown -->
      <div ref="dropdownRef" class="relative">
        <button
          class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-bg-secondary transition-colors duration-fast"
          :aria-expanded="dropdownOpen"
          aria-haspopup="true"
          data-testid="header-user-dropdown-trigger"
          @click="toggleDropdown"
        >
          <!-- Avatar -->
          <div
            class="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <span class="text-white text-xs font-semibold">{{ initials }}</span>
          </div>
          <span class="text-sm font-medium text-text-primary hidden sm:inline">
            {{ adminName }}
          </span>
          <ChevronDown
            :size="14"
            :class="[
              'text-text-muted transition-transform duration-fast hidden sm:inline',
              dropdownOpen ? 'rotate-180' : '',
            ]"
            aria-hidden="true"
          />
        </button>

        <!-- Dropdown menu -->
        <Transition
          enter-active-class="transition-all duration-fast ease-out"
          enter-from-class="opacity-0 scale-95 -translate-y-1"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-fast ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 -translate-y-1"
        >
          <div
            v-if="dropdownOpen"
            class="absolute right-0 top-full mt-1 w-64 bg-bg-primary border border-border rounded-lg shadow-dropdown z-dropdown"
            role="menu"
            data-testid="header-user-dropdown-menu"
          >
            <!-- User info section -->
            <div class="px-4 py-3 border-b border-border">
              <p class="text-sm font-semibold text-text-primary truncate" data-testid="header-admin-name">
                {{ adminName }}
              </p>
              <p class="text-xs text-text-muted truncate" data-testid="header-admin-email">
                {{ adminEmail }}
              </p>
              <span
                v-if="adminRole"
                :class="['inline-block mt-1 text-xs font-medium', roleBadgeClasses[adminRole]]"
                data-testid="header-admin-role"
              >
                {{ roleLabels[adminRole] }}
              </span>
            </div>

            <!-- Menu items -->
            <div class="py-1">
              <button
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger/10 transition-colors duration-fast"
                role="menuitem"
                data-testid="header-logout-btn"
                @click="handleLogout"
              >
                <LogOut :size="14" aria-hidden="true" />
                Logout
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>
